import { createContext, useContext, useState, useCallback, useMemo } from "react";
import DiscoveryGateModal from "@/components/DiscoveryGateModal";

/**
 * Site-wide replacement for the removed /contact page.
 *
 * Any component can call `useConnectModal()` to get `openConnectModal()`,
 * which opens the single shared DiscoveryGateModal instance mounted here.
 * This avoids every page needing its own `useState` + <DiscoveryGateModal />
 * pair just to wire up a "Speak with our team" CTA.
 */
const ConnectModalContext = createContext(null);

export function ConnectModalProvider({ children }) {
  const [open, setOpen] = useState(false);

  const openConnectModal = useCallback(() => setOpen(true), []);
  const closeConnectModal = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ openConnectModal, closeConnectModal }),
    [openConnectModal, closeConnectModal]
  );

  return (
    <ConnectModalContext.Provider value={value}>
      {children}
      <DiscoveryGateModal open={open} onOpenChange={setOpen} />
    </ConnectModalContext.Provider>
  );
}

export function useConnectModal() {
  const ctx = useContext(ConnectModalContext);
  if (!ctx) {
    throw new Error("useConnectModal must be used within a ConnectModalProvider");
  }
  return ctx;
}
